import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, Grid, CircularProgress,
} from '@material-ui/core';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import locale from 'date-fns/locale/en-US';
import { KeyboardDatePicker, MuiPickersUtilsProvider, KeyboardTimePicker } from '@material-ui/pickers';
import {
  Button, Input, Label, Select,
} from '@ipc/ipc-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { StyledModalForm } from '../../../../../common/list-table/helpers/formModal.styles';
import DateFormats from '../../../../../../elements/DateFormats';
import { getSelectedManualFlight, getSortedIataCodes, getIsManualFlightLoading } from '../../redux/selectors/FlightInformationSelectors';
import { getIataCodesRequestActionCreator } from '../../redux/actions/FlightInformationActions';
import { formatMessage } from '../../../../../../locales/locales';

function FlightInformationAddEditDialog(props) {
  const schedule = {
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
  };
  const [validTo, setValidTo] = useState(null);
  const [validFrom, setValidFrom] = useState(moment());
  const [carrierCode, setCarrierCode] = useState('');
  const [conveyanceRef, setConveyanceRef] = useState('');
  const [origin, setOrigin] = useState(null);
  const [flightSchedule, updateSchedule] = useState(schedule);
  const [destination, setDestination] = useState(null);
  const [isFormValid, updateFormValidity] = useState(false);
  const [minValidTo, updateMinValidTo] = useState(moment());
  const [isValidDatesEqual, updateValidDatesStatus] = useState(false);
  const [isValidToCurrent, updateValidToCurrentStatus] = useState(false);

  const {
    confirmSave,
    closeDialog,
    iataCodes,
    currentDestination,
    openDialog,
    selectedFlightToEdit,
    formMode,
    originIataCodes,
    getIataCodes,
    isFlightToEditLoading,
  } = props;

  useEffect(() => {
    if (destination !== currentDestination) {
      setDestination(currentDestination);
    }
  }, [currentDestination]);

  const updateFormToEdit = () => {
    setValidTo(selectedFlightToEdit.validTo ? moment(selectedFlightToEdit.validTo) : null);
    setValidFrom(moment(selectedFlightToEdit.validFrom));
    setCarrierCode(selectedFlightToEdit.carrierCode);
    setConveyanceRef(selectedFlightToEdit.conveyanceReference);
    setOrigin(
      { label: selectedFlightToEdit.origin, value: selectedFlightToEdit.origin },
    );
    setDestination(currentDestination);
    const currentFlightSchedule = {};
    Object.keys(schedule).forEach((key) => {
      currentFlightSchedule[key] = selectedFlightToEdit[key] ? moment(selectedFlightToEdit[key], 'HH:mm') : null;
    });
    updateSchedule(currentFlightSchedule);
  };

  useEffect(() => {
    if (formMode === 'EDIT' && openDialog && !isFlightToEditLoading) {
      updateFormToEdit();
    }
  }, [formMode, openDialog, selectedFlightToEdit]);

  const clearForm = () => {
    setValidTo(null);
    setValidFrom(moment());
    setCarrierCode('');
    setConveyanceRef('');
    setOrigin(null);
    updateSchedule(schedule);
    setDestination(null);
  };

  useEffect(() => {
    if (!openDialog) {
      clearForm();
    }
  }, [openDialog]);

  const checkValidity = () => {
    let isValid = true;
    if (validTo !== null && (!validTo.isValid() || validTo.isBefore(validFrom.startOf('day')))) {
      isValid = false;
    }
    if (((formMode !== 'EDIT' && !isFlightToEditLoading && validFrom.isBefore(moment().startOf('day')))
      || (formMode === 'EDIT' && !isFlightToEditLoading && moment(selectedFlightToEdit.validFrom).isAfter(moment().startOf('day'))
        && validFrom.isBefore(moment().startOf('day')))) || !validFrom.isValid()) {
      isValid = false;
    }
    if (carrierCode === '' || conveyanceRef === '' || !origin?.value || !destination?.value) {
      isValid = false;
    }
    if (!Object.keys(flightSchedule).find((item) => !!flightSchedule[item])) {
      isValid = false;
    }
    if (Object.keys(flightSchedule).find((item) => !!flightSchedule[item] && flightSchedule[item].toString() === 'Invalid Date')) {
      isValid = false;
    }
    if (validTo !== null && validTo.isValid() && validFrom.isValid() && validTo.isSameOrBefore(validFrom.startOf('day'))) {
      isValid = false;
    }
    if (validTo !== null && validTo.isValid() && validFrom.isValid() && validTo.isSameOrBefore(moment().startOf('day'))) {
      isValid = false;
    }
    updateValidDatesStatus(validTo !== null && validTo.isValid() && validFrom.isValid() && (validTo.startOf('day').isSameOrBefore(validFrom.startOf('day'))));
    updateValidToCurrentStatus(validTo !== null && validTo.isValid() && validFrom.isValid() && validTo.startOf('day').isSameOrBefore(moment().startOf('day')));
    return isValid;
  };

  useEffect(() => {
    updateFormValidity(checkValidity());
  }, [validFrom, validTo, carrierCode, conveyanceRef, origin, flightSchedule, destination]);

  useEffect(() => {
    const newMinValidTo = moment(validFrom.format(DateFormats.date), DateFormats.date).startOf('day').add(1, 'day');
    updateMinValidTo(moment().isAfter(newMinValidTo) ? moment().add(1, 'day') : newMinValidTo);
  }, [validFrom]);

  const onChangeValidTo = (newDate) => {
    if (!newDate) {
      setValidTo(null);
    } else {
      setValidTo(moment(newDate));
    }
  };

  const onChangeValidFrom = (newDate) => {
    setValidFrom(moment(newDate));
  };

  const handleCarrierCodeChange = (e) => {
    setCarrierCode(e.target.value);
  };

  const handleConveyanceRefChange = (e) => {
    setConveyanceRef(e.target.value);
  };
  const handleOriginSelection = (selection) => {
    setOrigin(selection);
  };

  const handleDestinationSelection = (selection) => {
    setDestination(selection);
  };

  const handleTimeChange = (flightTime, flightDay) => {
    const newSchedule = {
      ...flightSchedule,
      [flightDay]: flightTime,
    };
    updateSchedule(newSchedule);
  };

  const prepareRequestData = () => {
    const requestData = {};
    Object.keys(flightSchedule).forEach((item) => {
      requestData[item] = flightSchedule[item] ? moment(flightSchedule[item]).format('HH:mm') : null;
    });
    if (formMode === 'EDIT') {
      requestData.flightId = selectedFlightToEdit.flightId;
      if (!validFrom.isSameOrBefore(moment()) && !moment(selectedFlightToEdit.validFrom).isSame(validFrom)) {
        requestData.newValidFrom = validFrom.format('YYYY-MM-DD');
      }
    }

    return {
      ...requestData,
      validTo: validTo ? validTo.format('YYYY-MM-DD') : null,
      validFrom: validFrom.format('YYYY-MM-DD'),
      carrierCode,
      conveyance: conveyanceRef,
      destination: destination.value,
      origin: origin.value,
    };
  };

  const handleSubmit = () => {
    confirmSave(prepareRequestData());
  };

  const handleCancel = () => {
    closeDialog();
    clearForm();
  };

  const createLabel = (label) => `${label.charAt(0).toUpperCase()}${label.slice(1)}`;

  useEffect(() => {
    if (openDialog) {
      getIataCodes({ params: { iataSort: true } });
    }
  }, [openDialog]);

  return (
    <Dialog onClose={closeDialog} aria-labelledby="simple-dialog-title" open={openDialog}>
      <DialogTitle id="simple-dialog-title">{formMode === 'EDIT' ? 'Edit Flight' : 'Add New Flight'}</DialogTitle>
      <StyledModalForm>
        <table className="form-details-with-time-picker">
          <tbody>
            <tr>
              <td className="form-input-fields">
                <Label>Carrier Code*</Label>
              </td>
              <td className={formMode === 'EDIT' ? 'disabled-input form-input-fields' : 'form-input-fields'}>
                <Input
                  type="text"
                  id="carrierCode"
                  value={carrierCode}
                  minLength={1}
                  maxLength={3}
                  onChange={(e) => handleCarrierCodeChange(e)}
                  disabled={formMode === 'EDIT'}
                />
              </td>
              <td className="form-input-fields left-section">
                <Label>Conveyance ref*</Label>
              </td>
              <td className={formMode === 'EDIT' ? 'disabled-input form-input-fields' : 'form-input-fields'}>
                <Input
                  type="text"
                  id="conveyanceRef"
                  value={conveyanceRef}
                  minLength={1}
                  maxLength={4}
                  onChange={(e) => handleConveyanceRefChange(e)}
                  disabled={formMode === 'EDIT'}
                />
              </td>
            </tr>
            <tr>
              <td className="form-input-fields">
                <Label>Origin*</Label>
              </td>
              <td className={formMode === 'EDIT' ? 'disabled-input form-input-fields' : 'form-input-fields'}>
                {formMode !== 'EDIT' ? (
                  <Select
                    autoWidth
                    id="origin"
                    value={origin?.value}
                    placeholder={origin?.value}
                    options={originIataCodes}
                    onChange={handleOriginSelection}
                  />
                ) : (
                  <Input
                    type="text"
                    id="destination"
                    value={origin?.value}
                    disabled
                  />
                )}
              </td>
              <td className="form-input-fields left-section">
                <Label>Destination*</Label>
              </td>
              <td className={formMode === 'EDIT' ? 'disabled-input form-input-fields' : 'form-input-fields'}>
                {formMode !== 'EDIT' ? (
                  <Select
                    autoWidth
                    id="destination"
                    value={destination?.value}
                    placeholder={destination?.value}
                    options={iataCodes.map((item) => ({
                      label: item.iata,
                      value: item.iata,
                    }))}
                    onChange={handleDestinationSelection}
                    disabled={formMode === 'EDIT'}
                  />
                ) : (
                  <Input
                    type="text"
                    id="destination"
                    value={destination?.value}
                    disabled
                  />
                )}

              </td>
            </tr>
            <tr>
              <td className="form-input-fields">
                <Label>Valid from*</Label>
              </td>
              <td className="form-input-fields">
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                  <Grid container>
                    <KeyboardDatePicker
                      disablePast={formMode !== 'EDIT'}
                      disableToolbar
                      autoOk
                      format="dd-MM-yyyy"
                      margin="normal"
                      id="date-picker-inline from"
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      value={validFrom}
                      minDate={formMode === 'EDIT' && !isFlightToEditLoading && moment(selectedFlightToEdit.validFrom).isSameOrBefore(moment()) ? moment(selectedFlightToEdit.validFrom) : moment()}
                      onChange={onChangeValidFrom}
                      disabled={(formMode === 'EDIT' && !isFlightToEditLoading && moment(selectedFlightToEdit.validFrom).isSameOrBefore(moment())) || isFlightToEditLoading}
                    />
                  </Grid>
                </MuiPickersUtilsProvider>
              </td>
              <td className="form-input-fields left-section">
                <Label>Valid to</Label>
              </td>
              <td className="form-input-fields">
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
                  <Grid container>
                    <KeyboardDatePicker
                      disablePast={formMode !== 'EDIT'}
                      disableToolbar
                      autoOk
                      format="dd-MM-yyyy"
                      margin="normal"
                      id="date-picker-inline to"
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      minDate={minValidTo}
                      value={validTo}
                      onChange={onChangeValidTo}
                      disabled={isFlightToEditLoading}
                    />
                  </Grid>
                </MuiPickersUtilsProvider>
              </td>
            </tr>
          </tbody>
        </table>
        { isValidDatesEqual && (
          <div className="flight-validation-message">
            <span>
              {formatMessage('en', 'flight.add.edit.validation.validTo.after.validFrom')}
            </span>
          </div>
        )}
        { isValidToCurrent && (
          <div className="flight-validation-message">
            <span>
              {formatMessage('en', 'flight.add.edit.validation.validTo.after.today')}
            </span>
          </div>
        )}
        <DialogTitle id="simple-dialog-title">Provide scheduled flight times:</DialogTitle>
        <table className="form-details-with-time-picker time-picker-table">
          <tbody>
            {Object.keys(flightSchedule).map((item) => (
              <tr key={item}>
                <td className="form-input-fields">
                  <Label>{createLabel(item)}</Label>
                </td>
                <td className="form-input-fields">
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Grid container>
                      <KeyboardTimePicker
                        value={flightSchedule[item]}
                        autoOk
                        id={`time-picker-inline ${item}`}
                        KeyboardButtonProps={{
                          'aria-label': 'change time',
                        }}
                        format="HH:mm"
                        ampm={false}
                        onChange={(params) => handleTimeChange(params, item)}
                        disabled={isFlightToEditLoading}
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="after-box">
          <Button className="cancel" onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            className={`primary-button-${!isFormValid || isFlightToEditLoading} action`}
            disabled={!isFormValid || isFlightToEditLoading}
            id="save"
          >
            Save
            { isFlightToEditLoading && <CircularProgress size={15} /> }
          </Button>
        </div>

      </StyledModalForm>
    </Dialog>
  );
}

FlightInformationAddEditDialog.propTypes = {
  confirmSave: PropTypes.func,
  closeDialog: PropTypes.func,
  openDialog: PropTypes.bool,
  iataCodes: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  originIataCodes: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
  currentDestination: PropTypes.objectOf(PropTypes.string),
  selectedFlightToEdit: PropTypes.objectOf(PropTypes.any),
  formMode: PropTypes.string,
  getIataCodes: PropTypes.func,
  isFlightToEditLoading: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    iataCodes: getSortedIataCodes(state),
    selectedFlightToEdit: getSelectedManualFlight(state),
    isFlightToEditLoading: getIsManualFlightLoading(state),
  };
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getIataCodes: getIataCodesRequestActionCreator,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(FlightInformationAddEditDialog);
